import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  DocumentTextIcon,
  SparklesIcon,
  AcademicCapIcon,
  ChartBarIcon,
  BeakerIcon,
  BookOpenIcon,
  ArrowRightIcon,
  PlusIcon,
  FolderIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline'
import { useDocumentStore } from '@stores/documentStore'
import { useAuthStore } from '@stores/authStore'
import { CreateDocumentModal } from '@components/document/CreateDocumentModal'
import { logError } from '@utils/logger'

const HomePage: React.FC = () => {
  const navigate = useNavigate()
  const { createDocument } = useDocumentStore()
  const { isAuthenticated } = useAuthStore()
  const [showCreateModal, setShowCreateModal] = React.useState(false)

  const handleCreateDocument = async (title: string, documentType: string, content?: string): Promise<string> => {
    const newDoc = await createDocument({
      title,
      content: content || '',
      document_type: documentType as any,
    })
    navigate(`/editor/${newDoc.id}`)
    setShowCreateModal(false)
    return newDoc.id
  }

  const handleQuickStart = () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    setShowCreateModal(true)
  }

  const features = [
    {
      icon: DocumentTextIcon,
      title: 'LaTeX Editor',
      description: 'Full-featured LaTeX editor with live PDF preview and syntax highlighting',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: SparklesIcon,
      title: 'AI-Powered Writing',
      description: 'Get intelligent suggestions and assistance for your scientific writing',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: BookOpenIcon,
      title: 'Citation Management',
      description: 'Organize and insert citations seamlessly with BibTeX support',
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: ChartBarIcon,
      title: 'Data Visualization',
      description: 'Create and embed charts, graphs, and scientific figures',
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: BeakerIcon,
      title: 'Research Tools',
      description: 'Literature discovery, draft generation, and collaboration features',
      color: 'from-indigo-500 to-purple-500',
    },
    {
      icon: AcademicCapIcon,
      title: 'Academic Focus',
      description: 'Built specifically for researchers, scientists, and academics',
      color: 'from-teal-500 to-cyan-500',
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-primary-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-cyan-400/20 to-blue-400/20 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* Logo/Brand */}
            <div className="flex justify-center mb-8">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="flex items-center justify-center w-24 h-24 rounded-2xl bg-gradient-to-br from-teal-500/10 to-cyan-500/10 dark:from-teal-500/20 dark:to-cyan-500/20 border border-teal-500/20 dark:border-teal-400/30 shadow-xl overflow-hidden p-4"
              >
                <img 
                  src="/logos/favicon.svg" 
                  alt="SciDraft Logo" 
                  className="w-full h-full object-contain"
                />
              </motion.div>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6">
              <span className="bg-gradient-to-r from-teal-400 via-cyan-500 to-blue-500 bg-clip-text text-transparent">
                SciDraft
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-4 max-w-3xl mx-auto">
              The modern scientific writing platform
            </p>
            <p className="text-lg text-gray-500 dark:text-gray-400 mb-12 max-w-2xl mx-auto">
              Write, collaborate, and publish your research with AI-powered assistance, 
              LaTeX support, and seamless citation management.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleQuickStart}
                className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
              >
                <span>Get Started</span>
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              {isAuthenticated && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowCreateModal(true)}
                    className="px-8 py-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-white font-semibold rounded-xl shadow-lg hover:shadow-xl border-2 border-slate-200 dark:border-slate-700 transition-all duration-200 flex items-center gap-2"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>New Document</span>
                  </motion.button>
                  <Link
                    to="/discover"
                    className="px-8 py-4 bg-slate-100 dark:bg-slate-800 text-gray-900 dark:text-white font-semibold rounded-xl shadow-lg hover:shadow-xl border-2 border-slate-200 dark:border-slate-700 transition-all duration-200 flex items-center gap-2"
                  >
                    <BookOpenIcon className="w-5 h-5" />
                    <span>Discover</span>
                  </Link>
                </>
              )}
              {!isAuthenticated && (
                <Link
                  to="/login"
                  className="px-8 py-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-white font-semibold rounded-xl shadow-lg hover:shadow-xl border-2 border-slate-200 dark:border-slate-700 transition-all duration-200"
                >
                  Sign In
                </Link>
              )}
            </div>

            {/* Quick Stats */}
            {isAuthenticated && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="mt-16 flex flex-wrap justify-center gap-8"
              >
                <Link
                  to="/documents"
                  className="flex items-center gap-3 px-6 py-3 bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow group"
                >
                  <FolderIcon className="w-6 h-6 text-teal-500 group-hover:scale-110 transition-transform" />
                  <div className="text-left">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Your Documents</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">View All</div>
                  </div>
                  <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Everything you need for scientific writing
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Powerful tools designed specifically for researchers and academics
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ y: -8 }}
              className="group relative bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-100 dark:border-slate-700"
            >
              <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${feature.color} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-500 rounded-3xl p-12 md:p-16 text-center overflow-hidden"
        >
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to start writing?
            </h2>
            <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
              Join researchers worldwide who use SciDraft to create, collaborate, and publish their work.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleQuickStart}
              className="px-10 py-4 bg-white text-teal-600 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 mx-auto"
            >
              <span>Create New Document</span>
              <ArrowRightIcon className="w-5 h-5" />
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Create Document Modal */}
      <CreateDocumentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateDocument}
      />

      <style>{`
        .bg-grid-pattern {
          background-image: 
            linear-gradient(to right, rgba(0, 0, 0, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
          background-size: 20px 20px;
        }
        .dark .bg-grid-pattern {
          background-image: 
            linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
        }
      `}</style>
    </div>
  )
}

export default HomePage

